/**
 * @file itazura.js
 * @description いたずら投稿一覧の動的描画（6件以上で SimpleBar スクロール）
 */

import axios from "axios";
import SimpleBar from "simplebar";
import "simplebar/dist/simplebar.css";

const DEFAULT_IMAGE = "img-itazura01.jpg";
const SCROLL_THRESHOLD = 6; // これを超えたらスクロールバーを表示

/**
 * @typedef ItazuraItem
 * @property {number} id
 * @property {string} date
 * @property {string} text
 * @property {string} [img]
 * @property {string} [url]
 */

/**
 * itazura.json を取得
 * @param {string} url
 * @returns {Promise<ItazuraItem[]>}
 */
async function loadItazura(url) {
  try {
    const res = await axios.get(url, { params: { t: String(Date.now()) } });
    const data = res?.data;
    if (!Array.isArray(data)) {
      console.warn("[itazura] JSONが配列ではありません:", data);
      return [];
    }
    return data.filter(
      (item) =>
        item &&
        typeof item.id === "number" &&
        typeof item.date === "string" &&
        typeof item.text === "string",
    );
  } catch (err) {
    console.error("[itazura] 取得に失敗:", err);
    return [];
  }
}

/**
 * 画像パスを解決（本番ビルド時は webp に変換）
 * @param {string} imgBase
 * @param {string} [img]
 * @returns {string}
 */
function getImagePath(imgBase, img) {
  const filename = String(img || DEFAULT_IMAGE);
  const resolvedFilename = import.meta.env.PROD
    ? filename.replace(/\.(jpe?g|png)$/i, ".webp")
    : filename;
  return `${imgBase}/${resolvedFilename}`;
}

/**
 * HTMLエスケープ
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * date 文字列 "YYYY.MM.DD" を datetime 属性用の "YYYY-MM-DD" に変換
 * @param {string} date
 * @returns {string}
 */
function toDatetime(date) {
  return date.replace(/\./g, "-");
}

/**
 * リンク先URLが有効か判定
 * @param {string} [url]
 * @returns {boolean}
 */
function hasUrl(url) {
  return typeof url === "string" && url.trim() !== "";
}

/**
 * いたずらアイテムの共通コンテンツHTML
 * @param {ItazuraItem} item
 * @param {string} imgBase
 * @returns {string}
 */
function renderItemContent(item, imgBase) {
  return `
    <figure>
      <img
        src="${escapeHtml(getImagePath(imgBase, item.img))}"
        alt=""
        width="270"
        height="183"
        loading="lazy"
        decoding="async"
      >
    </figure>
    <figcaption>
      <!-- <time datetime="${escapeHtml(toDatetime(item.date))}">${escapeHtml(item.date)}</time> -->
      <p>${escapeHtml(item.text)}</p>
    </figcaption>
  `;
}

export class ItazuraList {
  /**
   * @param {object} options
   * @param {HTMLElement} options.sectionEl
   * @param {string} options.jsonUrl
   */
  constructor({ sectionEl, jsonUrl }) {
    this.sectionEl = sectionEl;
    this.jsonUrl = jsonUrl;
    this.listScrollEl = sectionEl.querySelector(".itazura-list-scroll");
    this.listEl = sectionEl.querySelector(".itazura-list");
    this.imgBase = sectionEl.dataset.imgBase || "./assets/img/itazura";
    this.simpleBar = null;
  }

  async init() {
    if (!this.listEl) {
      console.warn("[itazura] .itazura-list が見つかりません");
      return;
    }

    const items = await loadItazura(this.jsonUrl);
    if (items.length === 0) return;

    this.render(items);

    if (items.length > SCROLL_THRESHOLD) {
      this.initSimpleBar();
    } else {
      // スクロール不要な場合はスクロールラッパーの高さ制限を解除
      if (this.listScrollEl) {
        this.listScrollEl.classList.add("is-no-scroll");
      }
    }
  }

  render(items) {
    const fragment = document.createDocumentFragment();

    for (const item of items) {
      const li = document.createElement("li");
      const content = renderItemContent(item, this.imgBase);

      if (hasUrl(item.url)) {
        li.innerHTML = `
        <article class="itazura-item">
          <a href="${escapeHtml(item.url)}" target="_blank" rel="noopener noreferrer" itemprop="url">
            ${content}
          </a>
        </article>
      `;
      } else {
        li.innerHTML = `
        <article class="itazura-item">
          <div class="itazura-item__inner">
            ${content}
          </div>
        </article>
      `;
      }

      fragment.appendChild(li);
    }

    this.listEl.replaceChildren(fragment);
  }

  initSimpleBar() {
    if (!this.listScrollEl || this.simpleBar) return;

    this.simpleBar = new SimpleBar(this.listScrollEl, {
      autoHide: false,
    });
  }
}

/**
 * いたずら一覧を初期化
 * @param {object} options
 * @param {HTMLElement} options.sectionEl
 * @param {string} [options.jsonUrl]
 */
export async function initItazura({ sectionEl, jsonUrl = "./data/itazura.json" }) {
  if (!sectionEl) return;

  const list = new ItazuraList({ sectionEl, jsonUrl });
  await list.init();
}
